import axios from "axios";
import { message } from "antd";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

const AUTH_EXPIRED_MESSAGE_KEY = "auth-session-expired";
const PERMISSION_DENIED_MESSAGE_KEY = "permission-denied";

let isRefreshing = false;
let isSessionExpired = false;
let failedQueue = [];

const isUrlMatch = (config, path) => config?.url?.includes(path);
const isSignInRequest = (config) => isUrlMatch(config, "users/auth/sign-in");
const isRefreshRequest = (config) =>
  isUrlMatch(config, "users/auth/refresh-token");
const isSignOutRequest = (config) => isUrlMatch(config, "users/auth/sign-out");

const createAuthExpiredError = (sourceError) => {
  const authError = sourceError || new Error("AUTH_SESSION_EXPIRED");
  authError.__authExpired = true;
  return authError;
};

const isFormData = (data) =>
  typeof FormData !== "undefined" && data instanceof FormData;

const notifySessionExpired = () => {
  message.open({
    key: AUTH_EXPIRED_MESSAGE_KEY,
    type: "error",
    content: "Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.",
  });
};

const handleSessionExpired = (sourceError) => {
  const authError = createAuthExpiredError(sourceError);

  if (isSessionExpired) return authError;

  isSessionExpired = true;
  isRefreshing = false;
  delete axiosInstance.defaults.headers.common["Authorization"];
  axiosInstance._setAccessToken?.(null);
  processQueue(authError, null);
  notifySessionExpired();
  axiosInstance._onAuthExpired?.();

  return authError;
};

axiosInstance.interceptors.request.use(
  (config) => {
    if (isSessionExpired && !isSignInRequest(config)) {
      return Promise.reject(createAuthExpiredError());
    }

    if (isFormData(config.data) && config.headers) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
      delete config.headers.common?.["Content-Type"];
      delete config.headers.common?.["content-type"];
      delete config.headers.post?.["Content-Type"];
      delete config.headers.post?.["content-type"];
    }

    const token = axiosInstance._getAccessToken?.();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const processQueue = (error, newToken = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(newToken);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => {
    if (isSignInRequest(response.config) || isRefreshRequest(response.config)) {
      isSessionExpired = false;
    }

    // For blob responses (e.g. file downloads), return the raw data directly
    // to avoid unwrapping a Blob as if it were a JSON envelope.
    if (response.config?.responseType === "blob") {
      if (response.config?._returnFullResponse) return response;
      return response.data;
    }

    const resData = response?.data;
    if (response.config?._returnRawData) {
      return resData;
    }
    if (
      resData &&
      (resData.total !== undefined ||
        resData.stats !== undefined ||
        resData.page !== undefined ||
        resData.limit !== undefined)
    ) {
      return resData;
    }
    return resData?.data ?? resData;
  },

  async (error) => {
    const originalRequest = error.config;

    if (error.__authExpired) {
      return new Promise(() => {});
    }

    const isUnauthorized = error.response?.status === 401;
    const isForbidden = error.response?.status === 403;
    const isMethodNotAllowed = error.response?.status === 405;
    const isAlreadyRetried = originalRequest?._retry;
    const skipAuthRefresh =
      originalRequest?._skipAuthRefresh || isSignOutRequest(originalRequest);

    if (isForbidden) {
      const msg =
        error.response?.data?.message ||
        "Bạn không có quyền thực hiện chức năng này";
      // 403 của một thao tác con (thêm/sửa/xóa/gửi/...) chỉ hiển thị thông
      // báo và trả lỗi về component. Không điều hướng khỏi màn hình hiện tại.
      // Dùng cùng một key để tránh nhiều API chạy đồng thời tạo nhiều toast.
      error.status = 403;
      error.__permissionDenied = true;
      error.message = msg;
      message.open({
        key: PERMISSION_DENIED_MESSAGE_KEY,
        type: "error",
        content: msg,
      });
      return Promise.reject(error);
    }

    if (isMethodNotAllowed && !isRefreshRequest(originalRequest)) {
      const msg =
        error.response?.data?.message ||
        "Tài khoản đã bị khóa, Vui lòng liên hệ quản trị viên!";
      message.error(msg);
    }

    if (
      !(isUnauthorized || isMethodNotAllowed) ||
      isAlreadyRetried ||
      skipAuthRefresh ||
      isRefreshRequest(originalRequest)
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        })
        .catch((err) =>
          err.__authExpired ? new Promise(() => {}) : Promise.reject(err),
        );
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const response = await axiosInstance.post("users/auth/refresh-token");

      const newAccessToken = response?.accessToken;

      if (!newAccessToken) throw new Error("Không nhận được access token mới");

      isSessionExpired = false;
      axiosInstance._setAccessToken?.(newAccessToken);

      axiosInstance.defaults.headers.common["Authorization"] =
        `Bearer ${newAccessToken}`;

      processQueue(null, newAccessToken);

      originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      handleSessionExpired(refreshError);
      // Return a pending promise so the component doesn't receive an unhandled rejection
      // while the app is unmounting/redirecting to the sign-in page.
      return new Promise(() => {});
    } finally {
      isRefreshing = false;
    }
  },
);

export const setupAxiosInterceptors = ({
  getAccessToken,
  setAccessToken,
  onLogout,
  onAuthExpired,
}) => {
  axiosInstance._getAccessToken = getAccessToken;
  axiosInstance._setAccessToken = setAccessToken;
  axiosInstance._onLogout = onLogout;
  axiosInstance._onAuthExpired = onAuthExpired || onLogout;
  axiosInstance._markSessionActive = () => {
    isSessionExpired = false;
  };
};

export default axiosInstance;
